import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";

import { MunicipalitySetupChecklist } from "@/components/MunicipalitySetupChecklist";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { RecordPage } from "@/components/record/RecordPage";
import type { RecordField, RecordFieldProvenance } from "@/components/record/fields";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { ApiError, apiFetch } from "@/lib/api";
import type { MunicipalityResource } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";

/**
 * `/municipalities/:id` (Story 5.2, AD-9, AD-28). Built on `RecordPage`/
 * `FieldRow` (spec judgement call 7) — never a parallel page
 * implementation. Every business-data field is nullable (AD-9), so this
 * page's own `provenance` (below) can render AD-10's "this is a PropDesk
 * default" treatment directly from that nullability, reusing `FieldRow`'s
 * existing `provenance` prop wholesale rather than reimplementing it: a
 * field whose value is still `null` has never been confirmed and renders
 * `tier: "app_default"`; a field that has a real value renders
 * `tier: "municipality"` with the row's own `confirmed_on`.
 *
 * The five §5.5 checklist questions (rent control ordinance and cap,
 * continued-occupancy/CO inspection, landlord registration filing and
 * renewal trigger, tax portal vendor and lookup key, water/sewer biller and
 * cycle) map onto six real `Municipality` columns — the model has one field
 * each for the first, second, third and fifth questions
 * (`rent_control_cap`, `co_inspection_rule`, `registration_office`,
 * `water_sewer_biller`, each a free-text description covering the whole
 * question) and two for the fourth (`tax_portal_url` + `tax_lookup_format`).
 * While `setup_complete === false`, those six fields render *only* inside
 * the distinct checklist block below — Story 5.5 extracts that block into
 * the shared `MunicipalitySetupChecklist` component (never duplicated as
 * ordinary field rows too, which the spec's own judgement call 7 explicitly
 * rules out: "not five ordinary field rows indistinguishable from the rest
 * of the record") — every other business field renders as an ordinary field
 * row throughout. Once setup is complete, the checklist block disappears and
 * all twelve fields render uniformly as ordinary field rows.
 */

/** Every other business field, rendered as an ordinary field row
 * regardless of setup state. */
const ORDINARY_FIELD_KEYS: (keyof MunicipalityResource & string)[] = [
  "county",
  "county_tax_board",
  "appeal_deadline",
  "default_notice_period_days",
  "tax_grace_period_days",
  "received_vs_postmark_rule",
  "delinquent_interest_rate",
  "setup_complete",
  "confirmed_on",
];

/** AD-10's provenance, derived directly from this record's own nullability
 * (spec judgement call 7's own mechanism, applied at the municipality level
 * rather than through `settings_resolver.resolve()`, which answers this
 * question for a *property*'s inherited setting, not for the municipality
 * record itself): a field nobody has ever answered is `app_default`; a
 * field with a real value is `municipality`, confirmed on the row's own
 * `confirmed_on`. */
function provenanceFor(row: MunicipalityResource, key: keyof MunicipalityResource): RecordFieldProvenance | undefined {
  if (key === "county" || key === "setup_complete" || key === "confirmed_on") {
    return undefined;
  }
  const value = row[key];
  if (value === null) {
    return { tier: "app_default", confirmedOn: null };
  }
  return { tier: "municipality", confirmedOn: row.confirmed_on };
}

function formatBoolean(value: boolean): string {
  return value ? "Yes" : "No";
}

/** The value half of `PATCH /municipalities/{id}`'s body a commit
 * produces. An emptied text field is sent as `null` (clears the value back
 * to unanswered/`app_default`, AD-9's own nullable philosophy) rather than
 * as `""`, which the numeric fields' own `Decimal`/`int` validation would
 * otherwise refuse outright. */
function patchBodyFor(fieldKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for field "${fieldKey}"`);
  }
  return { [fieldKey]: payload.value === "" ? null : payload.value };
}

export function MunicipalityRecord() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  //: `PATCH /municipalities/{id}` is `role=Role.OWNER` (Tier A review,
  //: Story 5.2, Fix 2) — every edit affordance below must be withheld from
  //: anyone but a confirmed Owner, not merely "not Read-only". A Member is
  //: not Read-only, so `useIsReadOnly()` would have rendered these fully
  //: editable for a Member, who would then 403 the moment they submitted.
  const isOwner = useIsOwner();

  const queryKey = useMemo(() => ["municipalities", "record", id] as const, [id]);

  const recordQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<MunicipalityResource>(`/municipalities/${id}`),
    enabled: id !== undefined,
  });

  useEffect(() => {
    document.title = recordQuery.data ? `${recordQuery.data.name} — PropDesk` : "Municipality — PropDesk";
  }, [recordQuery.data]);

  function writeRecordToCache(updated: MunicipalityResource) {
    queryClient.setQueryData<MunicipalityResource>(queryKey, updated);
  }

  async function sendPatch(row: MunicipalityResource, field: DataTableColumn<MunicipalityResource>, payload: EditCommitPayload): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<MunicipalityResource>(`/municipalities/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchBodyFor(field.key, payload)),
      });
      writeRecordToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<MunicipalityResource>): Promise<EditCommitOutcome> {
    return sendPatch(args.row, args.column, args.payload);
  }

  const fields = useMemo<RecordField<MunicipalityResource>[]>(() => {
    const row = recordQuery.data;
    const definitions: RecordField<MunicipalityResource>[] = [];
    const includeChecklistFieldsAsOrdinaryRows = row === undefined || row.setup_complete;
    const orderedKeys = includeChecklistFieldsAsOrdinaryRows
      ? [
          "rent_control_cap",
          "co_inspection_rule",
          "registration_office",
          "tax_portal_url",
          "tax_lookup_format",
          "water_sewer_biller",
          ...ORDINARY_FIELD_KEYS,
        ]
      : ORDINARY_FIELD_KEYS;

    for (const key of orderedKeys as (keyof MunicipalityResource & string)[]) {
      if (key === "setup_complete") {
        definitions.push({ key, render: (r) => formatBoolean(r.setup_complete) });
        continue;
      }
      if (key === "county" || key === "confirmed_on") {
        definitions.push({ key });
        continue;
      }
      definitions.push({
        key,
        ...(isOwner ? { edit: { kind: "text" } } : {}),
        provenance: (r) => provenanceFor(r, key),
      });
    }
    return definitions;
  }, [recordQuery.data, isOwner]);

  const status: "loading" | "not-found" | "error" | "ready" = recordQuery.isPending
    ? "loading"
    : recordQuery.isError
      ? recordQuery.error instanceof ApiError && recordQuery.error.status === 404
        ? "not-found"
        : "error"
      : "ready";

  const errorMessage = recordQuery.error instanceof ApiError ? recordQuery.error.message : undefined;

  return (
    <div className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />
      {recordQuery.data && !recordQuery.data.setup_complete ? (
        <MunicipalitySetupChecklist
          municipality={recordQuery.data}
          isOwner={isOwner}
          onComplete={writeRecordToCache}
        />
      ) : null}

      <RecordPage<MunicipalityResource>
        fields={fields}
        row={recordQuery.data}
        status={status}
        {...(errorMessage ? { errorMessage } : {})}
        identifier={(row) => row.name}
        backLink={{ label: "Municipalities", href: "/settings/municipalities" }}
        onCommitEdit={commitEdit}
      />
    </div>
  );
}

export { provenanceFor };
